import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Navigate } from 'react-router-dom'
import { Icon } from '../components/ui/Icon'
import { SectionTabPanel, SectionTabs, useSectionTab } from '../components/ui/SectionTabs'
import { useAuth } from '../auth/AuthProvider'
import { useTenant } from '../tenancy/TenantProvider'
import { SWELL_HELP_CENTRE_URL, useLicenceDisplay } from '../tenancy/licence-display'
import { AuditHistoryPanel } from '../admin/AuditHistoryPanel'
import { loadPeopleAccessTransitionReadiness } from '../admin/people-access-transition-api'
import { peopleAccessSummary, peopleAccessSummaryLabel } from '../admin/people-access-summary'
import {
  cancelOrganisationInvitation,
  inviteOrganisationUser,
  loadOrganisationInvitations,
  loadOrganisationMembers,
  organisationRoleLabels,
  peopleAccessRoleCodes,
  resendOrganisationInvitation,
  setOrganisationPeopleAccessRoles,
  setOrganisationMembershipStatus,
  updateOrganisation,
  updateUserProfile,
  type OrganisationMember,
  type OrganisationRoleCode,
  type PeopleAccessRoleCode,
} from '../lib/governance-api'

function PageHeader({ eyebrow, title, description }: {
  eyebrow: string
  title: string
  description: string
}) {
  return (
    <header className="page-header">
      <div>
        <p className="eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
    </header>
  )
}

function RolePill({ role }: { role: OrganisationRoleCode }) {
  return <span className="role-pill">{organisationRoleLabels[role]}</span>
}

function membershipStatusLabel(status: string) {
  if (status === 'active') return 'Active'
  if (status === 'invited' || status === 'pending') return 'Invitation pending'
  return 'Inactive / Access removed'
}

export function ProfilePage() {
  const { profile, user, refreshIdentity } = useAuth()
  const [displayName, setDisplayName] = useState(profile?.displayName ?? '')
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => setDisplayName(profile?.displayName ?? ''), [profile?.displayName])

  const mutation = useMutation({
    mutationFn: () => updateUserProfile(displayName),
    onSuccess: async () => {
      await refreshIdentity()
      setMessage('Profile updated.')
      setError(null)
    },
    onError: (mutationError) => setError(mutationError instanceof Error ? mutationError.message : 'Unable to update profile.'),
  })

  return (
    <div className="page-stack narrow-page">
      <PageHeader eyebrow="Account" title="Your profile" description="Manage the name shown to other people in Swell. Your organisation roles are managed separately by an administrator." />
      <section className="panel">
        <form className="form-stack" onSubmit={(event) => { event.preventDefault(); mutation.mutate() }}>
          <label><span>Display name</span><input value={displayName} onChange={(event) => setDisplayName(event.target.value)} required /></label>
          <label><span>Email</span><input value={user?.email ?? ''} disabled /></label>
          {message ? <div className="inline-alert success">{message}</div> : null}
          {error ? <div className="inline-alert danger">{error}</div> : null}
          <div><button className="button primary" type="submit" disabled={mutation.isPending}>Save profile</button></div>
        </form>
      </section>
    </div>
  )
}

function OrganisationProfilePanel({ canManage }: { canManage: boolean }) {
  const { current, refreshOrganisations } = useTenant()
  const [name, setName] = useState(current?.organisation.name ?? '')
  const [sector, setSector] = useState(current?.organisation.sector ?? '')
  const [sizeBand, setSizeBand] = useState(current?.organisation.sizeBand ?? '')
  const [message, setMessage] = useState<string | null>(null)

  useEffect(() => {
    setName(current?.organisation.name ?? '')
    setSector(current?.organisation.sector ?? '')
    setSizeBand(current?.organisation.sizeBand ?? '')
  }, [current?.organisation.id, current?.organisation.name, current?.organisation.sector, current?.organisation.sizeBand])

  const mutation = useMutation({
    mutationFn: async () => {
      if (!current) throw new Error('Organisation is required')
      await updateOrganisation({ organisationId: current.organisation.id, name, sector, sizeBand })
    },
    onSuccess: async () => {
      await refreshOrganisations()
      setMessage('Organisation details updated.')
    },
  })

  if (!current) return null

  return (
    <section className="panel">
      <div className="section-heading">
        <div><p className="eyebrow">Organisation</p><h2>Organisation details</h2><p>{canManage ? 'Keep the basic organisation context Swell uses across governance workflows.' : 'These details provide context across your organisation’s Swell workspace.'}</p></div>
        <span className="status-chip neutral">Australia</span>
      </div>
      <form className="admin-form-grid" onSubmit={(event) => { event.preventDefault(); mutation.mutate() }}>
        <label><span>Organisation name</span><input value={name} onChange={(event) => setName(event.target.value)} required disabled={!canManage} /></label>
        <label><span>Sector</span><input value={sector} onChange={(event) => setSector(event.target.value)} disabled={!canManage} /></label>
        <label>
          <span>Approximate size</span>
          <select value={sizeBand} onChange={(event) => setSizeBand(event.target.value)} disabled={!canManage}>
            <option value="">Not specified</option>
            <option value="1-49">1–49 people</option>
            <option value="50-199">50–199 people</option>
            <option value="200-499">200–499 people</option>
            <option value="500-1999">500–1,999 people</option>
            <option value="2000+">2,000+ people</option>
          </select>
        </label>
        {canManage ? <div className="form-action-cell"><button className="button secondary" type="submit" disabled={mutation.isPending}>Save organisation</button></div> : null}
      </form>
      {message ? <div className="inline-alert success top-gap">{message}</div> : null}
      {mutation.error instanceof Error ? <div className="inline-alert danger top-gap">{mutation.error.message}</div> : null}
    </section>
  )
}

function OrganisationLicencePanel() {
  const { current } = useTenant()
  const licence = useLicenceDisplay(current?.service ?? {
    status: 'provisioning',
    permitted: false,
    customerMessage: null,
    serviceCommencementAt: null,
    trialEndsAt: null,
    serviceEndsAt: null,
    versionNo: 0,
  })

  if (!current) return null

  return (
    <section className="panel compact-account-panel">
      <div className="section-heading compact">
        <div><h2>Organisation &amp; licence</h2></div>
      </div>
      <dl className="account-detail-list">
        <div><dt>Organisation</dt><dd>{current.organisation.name}</dd></div>
        <div><dt>Licence</dt><dd>{licence.licenceLabel}</dd></div>
        {licence.trialEndLabel ? <div><dt>Trial ends</dt><dd>{licence.trialEndLabel}</dd></div> : null}
        {licence.timeRemainingLabel ? <div><dt>Time remaining</dt><dd>{licence.timeRemainingLabel}</dd></div> : null}
      </dl>
    </section>
  )
}

function SupportHelpPanel() {
  return (
    <section className="panel compact-account-panel support-help-panel">
      <div>
        <h2>Support &amp; help</h2>
        <p>Help, guidance and support</p>
      </div>
      <a className="button secondary" href={SWELL_HELP_CENTRE_URL} target="_blank" rel="noreferrer">Open help and support</a>
    </section>
  )
}

function MemberRow({ member, isCurrentUser, canManageAccess, canManageLifecycle, onChanged }: {
  member: OrganisationMember
  isCurrentUser: boolean
  canManageAccess: boolean
  canManageLifecycle: boolean
  onChanged: () => Promise<void>
}) {
  const [editing, setEditing] = useState(false)
  const [selectedRoles, setSelectedRoles] = useState<PeopleAccessRoleCode[]>(member.roles)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setSelectedRoles(member.roles)
    if (!canManageAccess) setEditing(false)
  }, [canManageAccess, member.roles])

  const rolesMutation = useMutation({
    mutationFn: () => setOrganisationPeopleAccessRoles(member.membershipId, selectedRoles),
    onSuccess: async () => { setEditing(false); setError(null); await onChanged() },
    onError: (mutationError) => setError(mutationError instanceof Error ? mutationError.message : 'Unable to update roles.'),
  })
  const statusMutation = useMutation({
    mutationFn: () => setOrganisationMembershipStatus(member.membershipId, member.status === 'active' ? 'suspended' : 'active'),
    onSuccess: onChanged,
    onError: (mutationError) => setError(mutationError instanceof Error ? mutationError.message : 'Unable to update membership.'),
  })

  const toggleRole = (role: PeopleAccessRoleCode) => {
    setSelectedRoles((currentRoles) => currentRoles.includes(role) ? currentRoles.filter((candidate) => candidate !== role) : [...currentRoles, role])
  }

  return (
    <article className="member-row">
      <div className="member-identity"><span className="avatar small-avatar">{member.displayName.slice(0, 1).toUpperCase()}</span><div><strong>{member.displayName}{isCurrentUser ? ' (you)' : ''}</strong><span>{member.email}</span><span className={`membership-status ${member.status}`}>{membershipStatusLabel(member.status)}</span></div></div>
      <div className="member-role-area">
        {canManageAccess && editing ? (
          <div className="role-editor">
            {peopleAccessRoleCodes.map((role) => (
              <label className="checkbox-row" key={role}><input type="checkbox" checked={selectedRoles.includes(role)} onChange={() => toggleRole(role)} disabled={isCurrentUser && role === 'organisation_admin'} /><span>{organisationRoleLabels[role]}</span></label>
            ))}
            {error ? <div className="inline-alert danger">{error}</div> : null}
            <div className="inline-actions"><button className="button primary compact" type="button" onClick={() => rolesMutation.mutate()} disabled={rolesMutation.isPending}>Save access</button><button className="button text compact" type="button" onClick={() => { setEditing(false); setSelectedRoles(member.roles) }}>Cancel</button></div>
          </div>
        ) : <div className="role-list">{member.roles.length ? member.roles.map((role) => <RolePill role={role} key={role} />) : <span className="muted">Standard member access</span>}</div>}
      </div>
      {canManageAccess || canManageLifecycle ? <div className="member-actions">
        {canManageAccess && !editing ? <button className="button text compact" type="button" onClick={() => setEditing(true)}><Icon name="edit" size={14} /><span>Manage access</span></button> : null}
        {canManageLifecycle && !isCurrentUser ? <button className="button text compact" type="button" onClick={() => statusMutation.mutate()} disabled={statusMutation.isPending}>{member.status === 'active' ? 'Suspend' : 'Reactivate'}</button> : null}
      </div> : null}
    </article>
  )
}

export function PeopleAccessPanel({ canManage }: { canManage: boolean }) {
  const { user } = useAuth()
  const { current } = useTenant()
  const queryClient = useQueryClient()
  const [displayName, setDisplayName] = useState('')
  const [email, setEmail] = useState('')
  const [makeAdministrator, setMakeAdministrator] = useState(false)
  const [invitationMessage, setInvitationMessage] = useState<{ text: string; tone: 'success' | 'warning' } | null>(null)
  const [cancelInvitationId, setCancelInvitationId] = useState<string | null>(null)
  const [transitionCheckSlow, setTransitionCheckSlow] = useState(false)
  const [transitionCheckAttempt, setTransitionCheckAttempt] = useState(0)
  const [peopleSearch, setPeopleSearch] = useState('')
  const [peopleStatus, setPeopleStatus] = useState<'active' | 'inactive' | 'all'>('active')
  const [peopleAccess, setPeopleAccess] = useState<'all' | 'administrator' | 'standard'>('all')
  const [showAllPeople, setShowAllPeople] = useState(false)
  const organisationId = current?.organisation.id

  const membersQuery = useQuery({ queryKey: ['organisation-members', organisationId], queryFn: () => loadOrganisationMembers(organisationId!), enabled: Boolean(organisationId) })
  const invitationsQuery = useQuery({ queryKey: ['organisation-invitations', organisationId], queryFn: () => loadOrganisationInvitations(organisationId!), enabled: Boolean(organisationId) })
  const transitionQuery = useQuery({
    queryKey: ['people-access-transition-readiness', organisationId, transitionCheckAttempt],
    queryFn: () => loadPeopleAccessTransitionReadiness(organisationId!),
    enabled: Boolean(organisationId && canManage),
    retry: false,
  })
  const transitionCheckPending = canManage && !transitionQuery.data && (transitionQuery.isLoading || transitionQuery.isFetching)
  const retryTransitionCheck = () => {
    setTransitionCheckSlow(false)
    setTransitionCheckAttempt((attempt) => attempt + 1)
  }

  useEffect(() => {
    if (!transitionCheckPending) {
      setTransitionCheckSlow(false)
      return
    }
    const timer = window.setTimeout(() => setTransitionCheckSlow(true), 8_000)
    return () => window.clearTimeout(timer)
  }, [transitionCheckPending])

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['organisation-members', organisationId] }),
      queryClient.invalidateQueries({ queryKey: ['organisation-invitations', organisationId] }),
    ])
  }

  const retryPeopleRead = async () => {
    await Promise.all([membersQuery.refetch(), invitationsQuery.refetch()])
  }

  const inviteMutation = useMutation({
    mutationFn: () => {
      if (!organisationId) throw new Error('Organisation is required')
      return inviteOrganisationUser({
        organisationId,
        displayName,
        email,
        roles: makeAdministrator ? ['organisation_admin'] : [],
      })
    },
    onSuccess: async (result) => {
      setDisplayName('')
      setEmail('')
      setMakeAdministrator(false)
      setInvitationMessage({
        text: result.warning ?? 'Invitation sent. The recipient must sign in before they can access Swell.',
        tone: result.warning ? 'warning' : 'success',
      })
      await refresh()
    },
  })

  const resendMutation = useMutation({
    mutationFn: (invitationId: string) => resendOrganisationInvitation(invitationId),
    onSuccess: async (result) => {
      setInvitationMessage({
        text: result.warning ?? 'A fresh sign-in link was sent and the invitation expiry was refreshed.',
        tone: result.warning ? 'warning' : 'success',
      })
      await refresh()
    },
  })

  const cancelMutation = useMutation({
    mutationFn: (invitationId: string) => cancelOrganisationInvitation(invitationId),
    onSuccess: async () => {
      setCancelInvitationId(null)
      setInvitationMessage({ text: 'Invitation cancelled. The record is retained in the organisation history and can no longer grant access.', tone: 'success' })
      await refresh()
    },
  })

  const pendingInvitations = useMemo(
    () => (invitationsQuery.data ?? []).filter(
      (invitation) => invitation.status === 'pending' && new Date(invitation.expiresAt).getTime() > Date.now(),
    ),
    [invitationsQuery.data],
  )
  const peopleReadSucceeded = membersQuery.isSuccess && invitationsQuery.isSuccess
  const peopleReadFailed = membersQuery.isError || invitationsQuery.isError
  const hasCachedPeople = membersQuery.data !== undefined || invitationsQuery.data !== undefined
  const accessSummary = peopleAccessSummary(membersQuery.data ?? [], invitationsQuery.data ?? [])
  const loadedAccessSummaryLabel = peopleAccessSummaryLabel(accessSummary)
  const memberCountLabel = peopleReadSucceeded
    ? loadedAccessSummaryLabel
    : membersQuery.data !== undefined
      ? `Last loaded · ${loadedAccessSummaryLabel}`
      : peopleReadFailed
        ? 'People unavailable'
        : 'Loading people…'
  const peopleFailureMessage = hasCachedPeople
    ? 'People could not be refreshed. Showing the last loaded information.'
    : 'People could not be loaded. Please try again.'
  const accessEditorReady = canManage && transitionQuery.isSuccess && transitionQuery.data.cutoverReady
  const unresolvedCount = transitionQuery.data?.unresolvedMembershipIds.length ?? 0
  const filteredMembers = (membersQuery.data ?? []).filter((member) => {
    const query = peopleSearch.trim().toLowerCase()
    if (query && !`${member.displayName} ${member.email}`.toLowerCase().includes(query)) return false
    if (peopleStatus === 'active' && member.status !== 'active') return false
    if (peopleStatus === 'inactive' && member.status === 'active') return false
    const isAdministrator = member.roles.includes('organisation_admin')
    if (peopleAccess === 'administrator' && !isAdministrator) return false
    if (peopleAccess === 'standard' && isAdministrator) return false
    return true
  })
  const visibleMembers = showAllPeople || peopleSearch ? filteredMembers : filteredMembers.slice(0, 10)
  const peopleAreLimited = visibleMembers.length < filteredMembers.length || peopleStatus !== 'all'

  return (
    <section className="panel">
      <div className="section-heading"><div><p className="eyebrow">Organisation</p><h2>People &amp; access</h2><p>See who can access Swell, who has been invited, and the organisation-level access they hold.</p></div><span className="status-chip neutral">{memberCountLabel}</span></div>
      {transitionCheckPending && !transitionCheckSlow ? (
        <div className="inline-alert neutral transition-readiness-alert" role="status"><strong>Checking whether this access change can be applied safely…</strong><span>This verifies that existing assignments and responsibilities will remain valid.</span></div>
      ) : null}
      {transitionCheckPending && transitionCheckSlow ? (
        <div className="inline-alert warning transition-readiness-alert" role="status">
          <strong>This check is taking longer than expected.</strong>
          <span>No access has changed. Retry the safety check, or open support if the delay continues.</span>
          <div className="inline-actions"><button className="button text compact" type="button" onClick={retryTransitionCheck}>Retry</button><a className="button text compact" href={SWELL_HELP_CENTRE_URL} target="_blank" rel="noreferrer">Open Swell Help &amp; Support ↗</a></div>
        </div>
      ) : null}
      {canManage && transitionQuery.isError ? (
        <div className="inline-alert danger transition-readiness-alert" role="alert">
          <strong>The access safety check could not be completed.</strong>
          <span>Access role changes remain unavailable until the safety check succeeds. No access has changed.</span>
          <div className="inline-actions"><button className="button text compact" type="button" onClick={retryTransitionCheck}>Retry</button><a className="button text compact" href={SWELL_HELP_CENTRE_URL} target="_blank" rel="noreferrer">Open Swell Help &amp; Support ↗</a></div>
        </div>
      ) : null}
      {canManage && transitionQuery.isSuccess && !transitionQuery.data.cutoverReady ? (
        <div className="inline-alert warning" role="status">
          <strong>Legacy access decisions are required before role changes can be made.</strong>
          <span>{unresolvedCount} {unresolvedCount === 1 ? 'person still has' : 'people still have'} legacy Governance Manager or Executive Viewer access that needs an explicit transition decision. Existing legacy access remains visible below and has not been changed.</span>
        </div>
      ) : null}
      {canManage && accessEditorReady ? (
        <div className="inline-alert success" role="status">Access transition is ready. New invitations use Standard member access with optional Organisation Administrator capability.</div>
      ) : null}
      {accessEditorReady ? <div className="invite-box">
        <div className="invite-intro"><span className="feature-icon"><Icon name="user" size={20} /></span><div><h3>Invite person</h3><p className="muted">Invite someone when they need access to Swell. Governance responsibilities are assigned later from the relevant workflow.</p></div></div>
        <form className="invite-form" onSubmit={(event) => { event.preventDefault(); setInvitationMessage(null); inviteMutation.mutate() }}>
          <label><span>Name</span><input value={displayName} onChange={(event) => setDisplayName(event.target.value)} placeholder="Michael Chen" required /></label>
          <label className="invite-email-field"><span>Work email address</span><input type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="person@organisation.com.au" required /></label>
          <fieldset className="role-checkbox-grid"><legend>Optional administration access</legend><label className="checkbox-row"><input type="checkbox" checked={makeAdministrator} onChange={(event) => setMakeAdministrator(event.target.checked)} /><span>Make this person an Organisation Administrator</span></label></fieldset>
          <div className="full-width">
            <p className="muted">Everyone with active Organisation membership can view ordinary same-tenant AI uses and governance records. Questions, confirmations and approvals still belong to the named current assignee.</p>
            <p className="muted"><strong>Restricted Evidence is an exception.</strong> It is visible only where a separate current source authority permits it. Standard membership and Organisation Administrator access do not grant that Evidence access.</p>
            <p className="muted">Organisation Administrators can manage people, organisation settings and governance assignments. They cannot answer or decide work assigned to someone else.</p>
          </div>
          {invitationMessage ? <div className={`inline-alert ${invitationMessage.tone} full-width`} role="status">{invitationMessage.text}</div> : null}
          {inviteMutation.error instanceof Error ? <div className="inline-alert danger full-width">{inviteMutation.error.message}</div> : null}
          {resendMutation.error instanceof Error ? <div className="inline-alert danger full-width">{resendMutation.error.message}</div> : null}
          {cancelMutation.error instanceof Error ? <div className="inline-alert danger full-width">{cancelMutation.error.message}</div> : null}
          <div className="full-width"><button className="button primary" type="submit" disabled={inviteMutation.isPending}><Icon name="send" size={15} /><span>{inviteMutation.isPending ? 'Sending invitation…' : 'Send invitation'}</span></button></div>
        </form>
      </div> : !canManage ? <div className="inline-alert neutral">People &amp; access is read-only for Governance Managers. Organisation Administrators manage invitations and organisation access.</div> : null}
      {peopleReadFailed ? (
        <div className="inline-alert danger" role="alert">
          <span>{peopleFailureMessage}</span>
          <div className="inline-actions">
            <button className="button text compact" type="button" onClick={() => void retryPeopleRead()} disabled={membersQuery.isFetching || invitationsQuery.isFetching}>Retry</button>
          </div>
        </div>
      ) : null}
      {!hasCachedPeople && (membersQuery.isLoading || invitationsQuery.isLoading) ? <div className="loading-line">Loading people and invitations…</div> : null}
      {pendingInvitations.length > 0 ? (
        <div className="pending-invitations">
          <h3>Pending invitations</h3>
          {pendingInvitations.map((invitation) => {
            const confirmingCancellation = cancelInvitationId === invitation.id
            const resendingThisInvitation = resendMutation.isPending && resendMutation.variables === invitation.id
            const cancellingThisInvitation = cancelMutation.isPending && cancelMutation.variables === invitation.id

            return (
              <div className="pending-invite-row" key={invitation.id}>
                <div><strong>{invitation.displayName}</strong><span>{invitation.email}</span><span>{membershipStatusLabel(invitation.status)} · Expires {new Date(invitation.expiresAt).toLocaleDateString('en-AU')}</span></div>
                <div className="role-list compact-list">{invitation.roles.length ? invitation.roles.map((role) => <RolePill role={role} key={role} />) : <span className="muted">Standard member access</span>}</div>
                {canManage ? <div className="member-actions">
                  {confirmingCancellation ? (
                    <>
                      <span className="muted">Cancel invitation for {invitation.displayName}? They will no longer be able to use it. The history will be retained.</span>
                      <button className="button danger-outline compact" type="button" onClick={() => cancelMutation.mutate(invitation.id)} disabled={cancellingThisInvitation}><Icon name="cancel" size={14} /><span>Confirm cancel</span></button>
                      <button className="button text compact" type="button" onClick={() => setCancelInvitationId(null)} disabled={cancellingThisInvitation}>Keep invitation</button>
                    </>
                  ) : (
                    <>
                      <button className="button text compact" type="button" onClick={() => { setInvitationMessage(null); resendMutation.mutate(invitation.id) }} disabled={resendMutation.isPending || cancelMutation.isPending}><Icon name="resend" size={14} /><span>{resendingThisInvitation ? 'Resending…' : 'Resend'}</span></button>
                      <button className="button text danger-text compact" type="button" onClick={() => setCancelInvitationId(invitation.id)} disabled={resendMutation.isPending || cancelMutation.isPending}><Icon name="cancel" size={14} /><span>Cancel invitation</span></button>
                    </>
                  )}
                </div> : null}
              </div>
            )
          })}
        </div>
      ) : peopleReadSucceeded ? <div className="empty-state"><h3>No other people have been added yet</h3><p>Invite someone when they need access to Swell. People who are asked to complete specific governance work can also be invited from that workflow once contextual assignment is enabled.</p></div> : null}
      <div className="people-access-directory">
        <div className="section-heading compact"><div><h3>People with access</h3><p>Active people are shown first. Search or change the filters to find someone else.</p></div></div>
        <div className="people-access-filters" aria-label="Filter people">
          <label><span>Search people</span><input value={peopleSearch} onChange={(event) => { setPeopleSearch(event.target.value); setShowAllPeople(true) }} placeholder="Name or email" /></label>
          <label><span>Status</span><select value={peopleStatus} onChange={(event) => { setPeopleStatus(event.target.value as typeof peopleStatus); setShowAllPeople(false) }}><option value="active">Active</option><option value="inactive">Inactive</option><option value="all">All statuses</option></select></label>
          <label><span>Access</span><select value={peopleAccess} onChange={(event) => { setPeopleAccess(event.target.value as typeof peopleAccess); setShowAllPeople(false) }}><option value="all">All access</option><option value="administrator">Organisation Administrators</option><option value="standard">Standard members</option></select></label>
        </div>
        <div className="member-list">
          {visibleMembers.map((member) => <MemberRow key={member.membershipId} member={member} isCurrentUser={member.userId === user?.id} canManageAccess={accessEditorReady} canManageLifecycle={canManage} onChanged={refresh} />)}
          {peopleReadSucceeded && visibleMembers.length === 0 ? <div className="empty-state"><h3>No people match these filters</h3><p>Change the search or filters to see other people with access.</p></div> : null}
        </div>
        {peopleAreLimited ? <button className="button secondary" type="button" onClick={() => { setPeopleStatus('all'); setPeopleAccess('all'); setShowAllPeople(true) }}>View all people</button> : null}
      </div>
    </section>
  )
}

export function AdministrationPage() {
  const { current, hasRole } = useTenant()
  const canManage = hasRole('organisation_admin')
  const canReadGovernance = hasRole('governance_manager')
  const canReadPeopleAccess = canManage || canReadGovernance
  const tabs = canReadPeopleAccess
    ? [{ id: 'organisation' as const, label: 'Organisation' }, { id: 'people' as const, label: 'People & access' }, { id: 'audit' as const, label: 'Audit history' }]
    : [{ id: 'organisation' as const, label: 'Organisation' }]
  const [activeTab, setActiveTab] = useSectionTab(tabs, canReadPeopleAccess ? 'people' : 'organisation')
  if (!current) return <Navigate to="/workspace/create" replace />

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Administration"
        title="Organisation administration"
        description={canManage
          ? 'Manage your organisation details, people and Swell access, and inspect read-only audit history. Governance responsibilities and approvals are assigned from the relevant AI Use or workflow.'
          : canReadPeopleAccess
            ? 'View organisation details, People & access and read-only audit history. Organisation Administrators manage people and settings; governance responsibilities and approvals are assigned from the relevant AI Use or workflow.'
            : 'View your organisation details and understand your access. Organisation Administrators manage people and settings.'}
      />
      <section className="panel">
          <div className="section-heading">
            <div>
              <p className="eyebrow">Your access</p>
              <h2>{canManage ? 'You can administer this organisation' : 'You can view the complete application'}</h2>
              <p>{canManage ? 'Your Organisation Administrator access allows you to manage people and organisation settings. Governance confirmations and approvals still belong to the named people assigned from the relevant AI Use or workflow.' : 'Every active organisation user can open Overview, AI Register, AI Risks, Strategy & Roadmap, Reports and Administration. Roles control platform actions such as editing settings, publishing strategy and generating report snapshots. Governance confirmations and approvals are assigned to named people from the relevant AI Use or workflow.'}</p>
            </div>
            <span className="status-chip neutral">{canManage ? 'Administration access' : 'View all areas'}</span>
          </div>
          <div className="role-list">
            {current.roles.length
              ? current.roles.map((role) => <RolePill role={role} key={role} />)
              : <span className="status-chip neutral">Organisation member</span>}
          </div>
      </section>
      <SectionTabs tabs={tabs} activeTab={activeTab} onSelect={setActiveTab} label="Organisation administration sections" idPrefix="administration" />
      <SectionTabPanel idPrefix="administration" tab="organisation" activeTab={activeTab}>
        <OrganisationProfilePanel canManage={canManage} />
        <OrganisationLicencePanel />
        <SupportHelpPanel />
      </SectionTabPanel>
      {canReadPeopleAccess ? <SectionTabPanel idPrefix="administration" tab="people" activeTab={activeTab}>
        <PeopleAccessPanel canManage={canManage} />
      </SectionTabPanel> : null}
      {canReadPeopleAccess ? <SectionTabPanel idPrefix="administration" tab="audit" activeTab={activeTab}>
        <AuditHistoryPanel organisationId={current.organisation.id} canReadGovernance={canReadGovernance} />
      </SectionTabPanel> : null}
    </div>
  )
}
